/**
 * Sealed-key store for the master key / private-key KEK (`dk`).
 *
 * `dk` used to be persisted in `localStorage` in plaintext, so a snapshot of
 * storage (XSS exfiltration, a stolen disk image, a backup) contained the exact
 * key that unwraps the private key -> every CSK -> every message, usable offline
 * forever.
 *
 * Instead we seal `dk` under a **non-extractable** WebCrypto AES-GCM key that
 * lives in a dedicated IndexedDB. The browser hands back a *usable but
 * unreadable* key handle: `crypto.subtle` can decrypt with it, but `exportKey`
 * throws and the raw bytes are never in storage. So an offline attacker who
 * copies IndexedDB + localStorage gets only ciphertext and an unusable handle.
 *
 * What this does NOT stop: a *live* XSS running in the origin can still call
 * `unsealDk()` (the handle is usable in-page) — defeating live script injection
 * is a separate CSP/sanitisation track. This closes the far larger
 * offline-exfiltration hole.
 *
 * Isolated in its own database (`orion-keyvault`, not the message `odb`) so it is
 * untouched by `clearLocalDB` and message-DB migrations; its lifecycle is driven
 * explicitly by seal (login) / unseal (reload) / clear (logout).
 */

const DB_NAME = "orion-keyvault";
const STORE = "vault";
const RECORD_KEY = "dk";

type SealedRecord = {
  key: CryptoKey; // non-extractable AES-GCM key (structured-cloned into IDB)
  iv: Uint8Array;
  ct: ArrayBuffer; // dk (hex string, UTF-8) encrypted under `key`
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(k: string): Promise<SealedRecord | undefined> {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).get(k);
      req.onsuccess = () => resolve(req.result as SealedRecord | undefined);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

async function idbPut(k: string, v: SealedRecord): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(v, k);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function idbDelete(k: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(k);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/**
 * Seal `dk` (hex): generate a fresh non-extractable AES-GCM key, encrypt `dk`
 * under it, and store both in IndexedDB. Overwrites any prior seal.
 */
export async function sealDk(dkHex: string): Promise<void> {
  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    /* extractable */ false,
    ["encrypt", "decrypt"],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(dkHex),
  );
  await idbPut(RECORD_KEY, { key, iv, ct });
}

/**
 * Recover `dk` (hex) from the sealed record, or `null` if there is no seal or it
 * can't be decrypted (cleared IDB, corruption). Never throws.
 */
export async function unsealDk(): Promise<string | null> {
  try {
    const rec = await idbGet(RECORD_KEY);
    if (!rec) {
      return null;
    }
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: rec.iv },
      rec.key,
      rec.ct,
    );
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}

/** Is a sealed `dk` present? Used to seal legacy/first-login keys exactly once. */
export async function hasSealedDk(): Promise<boolean> {
  try {
    return (await idbGet(RECORD_KEY)) !== undefined;
  } catch {
    return false;
  }
}

/** Remove the sealed key. Called on logout so nothing recoverable is left. */
export async function clearSealedDk(): Promise<void> {
  try {
    await idbDelete(RECORD_KEY);
  } catch {
    // Best-effort: a failure here can't leak the key (it's non-extractable) and
    // the record is overwritten on the next login.
  }
}
